import api from ".";
import generateHeaders from "../utils/generateHeaders.js";

/**
 * Get insurance analytics data with filtering, pagination, and sorting
 * @param {Object} params
 * @param {string} params.token - JWT authentication token
 * @param {number} params.eventId - Event ID filter (optional)
 * @param {number} params.uploadFlowId - Upload flow ID filter (optional)
 * @param {number} params.documentConfigId - Document config ID filter (optional)
 * @param {number} params.page - Page number (0-indexed, default: 0)
 * @param {number} params.size - Items per page (default: 20)
 * @param {string} params.sortBy - Sort field (default: 'requiredInsurances')
 * @param {string} params.sortDirection - 'ASC' or 'DESC' (default: 'DESC')
 * @returns {Promise} Response with pagination info and items
 */
export const getInsuranceAnalyticsService = async ({
  token,
  eventId,
  uploadFlowId,
  documentConfigId,
  year,
  page = 0,
  size = 20,
  sortBy = 'requiredInsurances',
  sortDirection = 'DESC',
}) => {
  const headers = generateHeaders(token);

  const params = {
    page,
    size,
    sortBy,
    sortDirection,
  };

  // Add optional filters only if they have values
  if (eventId) params.eventId = eventId;
  if (uploadFlowId) params.uploadFlowId = uploadFlowId;
  if (documentConfigId) params.documentConfigId = documentConfigId;
  if (year) params.year = year;

  return await api.get('/upload-flow/insurance-analytics', {
    ...headers,
    params,
  });
};

/**
 * Get insurance dashboard filter options with cross-filtering support
 * @param {Object} params
 * @param {string} params.token - JWT authentication token
 * @param {number} params.eventId - Selected event ID (optional, filters flows/documents)
 * @param {number} params.uploadFlowId - Selected flow ID (optional, filters events/documents)
 * @param {number} params.documentConfigId - Selected document ID (optional, filters events/flows)
 * @returns {Promise} Response with events, uploadFlows, and documentConfigs arrays
 *   - documentConfigs includes: { id, fileName, uploadFlowName } to differentiate same-named files
 */
export const getInsuranceDashboardFiltersService = async ({
  token,
  eventId,
  uploadFlowId,
  documentConfigId,
}) => {
  const headers = generateHeaders(token);

  const params = {};

  // Add optional filter IDs only if they have values
  if (eventId) params.eventId = eventId;
  if (uploadFlowId) params.uploadFlowId = uploadFlowId;
  if (documentConfigId) params.documentConfigId = documentConfigId;

  return await api.get('/upload-flow/insurance-dashboard/filters', {
    ...headers,
    params,
  });
};
